import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { setVehicles, setVehicleTypes } from "../../../store/actions/project";
import { setSolution } from "../../../store/actions/solution";
import { capitalize, remCalc } from "../../../utils/helpers";
import { Box, makeStyles } from "@material-ui/core";
import { withSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import BackIcon from "@material-ui/icons/ArrowBack";
import Grid from "@material-ui/core/Grid";
import API from "../../../utils/axios";
import StyledDrawer from "../StyledDrawer";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import Form from "../../common/Form";
import {
  defaultCapacity,
  defaultWeight,
  defaultVolume
} from "../../data/constants";
import { numberValidationRegex } from "../../../utils/regExps";
import EditDialogActions from "../../common/EditDialogActions";
import mixpanelWrapper from "../../../utils/mixpanel";

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    borderBottom: "1px solid #eee",
    boxSizing: "border-box",
    flexShrink: 0
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden"
  },
  subtitle: {
    lineHeight: "1.1875rem",
    fontWeight: 500,
    marginRight: theme.spacing(0.5)
  },
  button: {
    padding: theme.spacing(1, 1.125),
    minWidth: remCalc(46)
  },
  panelError: {
    fontSize: "0.6875rem",
    color: "#D93024"
  }
}));

function EditVehicle({
  open,
  vehicles,
  vehicleTypes,
  setVehicles,
  solution,
  setSolution,
  setCurrentAction,
  setCurrentVehicle,
  setVehicleTypes,
  enqueueSnackbar,
  currentVehicleId,
  saveDisabled,
  setSaveDisabled,
  drivers,
  tags,
  sortedTags,
  setBackFromEditAction
}) {
  const [vehicleType, setVehicleType] = useState({});
  const inputRef = useRef();

  const [selectedTags, setSelectedTags] = useState(
    vehicleType && vehicleType.tags
      ? vehicleType.tags
          .filter(currentTag => tags.find(tag => tag.id === currentTag.id))
          .map(tag => tag.id)
      : []
  );

  useEffect(() => {
    const currentVehicleType =
      vehicleTypes.find(vehicleType => vehicleType.id === currentVehicleId) ||
      {};
    setVehicleType(currentVehicleType);
    const defaultDriver = currentVehicleType.default_driver
      ? {
          name: `${currentVehicleType.default_driver.first_name} ${currentVehicleType.default_driver.last_name}`,
          id: currentVehicleType.default_driver.id
        }
      : null;
    setInputValues({
      name: currentVehicleType.name || "",
      capacity:
        !currentVehicleType.quantity ||
        currentVehicleType.quantity === defaultCapacity
          ? ""
          : currentVehicleType.quantity,
      weight:
        !currentVehicleType.weight ||
        currentVehicleType.weight === defaultWeight
          ? ""
          : currentVehicleType.weight,
      volume:
        !currentVehicleType.volume ||
        currentVehicleType.volume === defaultVolume
          ? ""
          : currentVehicleType.volume,
      profile:
        !currentVehicleType.profile || currentVehicleType.profile === "default"
          ? "car"
          : currentVehicleType.profile,
      tags: currentVehicleType.tags,
      defaultDriver,
      defaultStartLocation: currentVehicleType.start_address,
      defaultEndLocation: currentVehicleType.end_address
    });

    setSelectedTags(
      currentVehicleType && currentVehicleType.tags
        ? currentVehicleType.tags
            .filter(currentTag => tags.find(tag => tag.id === currentTag.id))
            .map(tag => tag.id)
        : []
    );

    setErrors({
      name: "",
      load: "",
      defaultStartLocation: ""
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVehicleId, open]);

  const [loading, setLoading] = useState(false);

  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    action: () => setCurrentAction("overview")
  });

  const classes = useStyles();
  const history = useHistory();

  // Get capacity, weight, volume from vehicle types
  // defaultCapacity, defaultWeight, defaultVolume are default values

  const capacity = vehicleType.quantity;
  const weight = vehicleType.weight;
  const volume = vehicleType.volume;
  const defaultDriver = vehicleType.default_driver
    ? {
        name: `${vehicleType.default_driver.first_name} ${vehicleType.default_driver.last_name} (${vehicleType.default_driver.email})`,
        id: vehicleType.default_driver.id
      }
    : null;

  const [inputValues, setInputValues] = useState({
    capacity: !capacity || capacity === defaultCapacity ? "" : capacity,
    weight: !weight || weight === defaultWeight ? "" : weight,
    volume: !volume || volume === defaultVolume ? "" : volume,
    name: vehicleType.name || "",
    profile:
      !vehicleType.profile || vehicleType.profile === "default"
        ? "car"
        : vehicleType.profile,
    defaultDriver,
    defaultStartLocation: vehicleType.start_address,
    defaultEndLocation: vehicleType.end_address
  });

  const [errors, setErrors] = useState({
    name: "",
    defaultStartLocation: ""
  });

  // Handle input fields' changes
  const handleChange = e => {
    let { name, value } = e.target;

    if (["capacity", "weight", "volume"].includes(name)) {
      if (value === "0") {
        value = "";
      } else if (value.includes("-")) {
        value = value.split("-").join("");
      }
    }

    if (("" + inputValues[name]).trim() !== value.trim()) {
      setSaveDisabled(false);
    }
    setInputValues({ ...inputValues, [name]: value });
  };

  // Close edit panel
  const handleClose = () => {
    setLoading(false);
    setCurrentVehicle(null);
    setCurrentAction("overview");
  };

  // Handle save
  const saveVehicleChanges = () => {
    const body = {
      name: inputValues.name.trim(),
      quantity: (inputValues.capacity + "").trim() || defaultCapacity,
      weight: (inputValues.weight + "").trim() || defaultWeight,
      volume: (inputValues.volume + "").trim() || defaultVolume,
      profile: inputValues.profile || "car",
      tags: selectedTags,
      default_driver_id: inputValues.defaultDriver?.id || null,
      start_address: inputValues.defaultStartLocation || null,
      end_address: inputValues.defaultEndLocation || null
    };

    // Update store
    const newVehicleTypes = [...vehicleTypes];
    const nextIndex = vehicleTypes.length;

    // API call
    if (currentVehicleId && vehicleType.id) {
      // Update vehicle
      API.patch(`/v1/vehicles/${currentVehicleId}`, body)
        .then(res => {
          handleUpdate(res.data, "edited");
        })
        .catch(error => {
          setLoading(false);
          enqueueSnackbar("Something went wrong, couldn't update vehicle");
          console.log(error);
        });
    } else {
      // Add vehicle
      API.post("/v1/vehicles", body)
        .then(res => {
          const vehicleType = res.data;
          newVehicleTypes[nextIndex] = vehicleType;
          setVehicleTypes(newVehicleTypes);
          mixpanelWrapper.track("Vehicle Add");
          handleUpdate(res.data, "added");
        })
        .catch(error => {
          setLoading(false);
          enqueueSnackbar("Something went wrong, couldn't add vehicle");
          console.log(error);
        });
    }

    const handleUpdate = (newVehicleType, action) => {
      if (action === "edited") {
        const index = vehicles.findIndex(
          vehicle => vehicle?.type_id?.id === newVehicleType.id
        );

        if (index !== -1) {
          const newVehicles = [...vehicles];
          newVehicles[index] = {
            ...newVehicles[index],
            type_id: newVehicleType
          };
          if (solution) {
            const newSolution = {
              ...solution,
              routes: solution.routes.map(route => {
                if (route.vehicle_id?.includes(newVehicles[index].vehicle_id)) {
                  return { ...route, vehicle_name: newVehicleType.name };
                } else {
                  return route;
                }
              })
            };
            setSolution(newSolution);
          }
          setVehicles(newVehicles);
        }

        const typeIndex = vehicleTypes.findIndex(
          vehicleType => vehicleType.id === newVehicleType.id
        );

        const newVehicleTypes = [...vehicleTypes];
        newVehicleTypes[typeIndex] = newVehicleType;

        setVehicleTypes(newVehicleTypes);
      }
      enqueueSnackbar(`Vehicle "${inputValues.name}" ${action}.`);
      setSaveDisabled(true);
      handleClose();
    };
  };

  const checkForErrors = (name, errors) => {
    if (!inputValues[name]) return;
    if (
      !Number.isInteger(+`${inputValues[name]}`?.trim()) ||
      Number(`${inputValues[name]}`.trim()) < 1
    ) {
      errors[name] = `${capitalize(name)} should be a natural number`;
    }

    if (Number(`${inputValues[name]}`.trim()) > 1000000) {
      errors[name] = `${capitalize(name)} is too high`;
    }
  };

  // Validate inputs and save changes
  const handleSave = () => {
    setLoading(true);
    const { name } = inputValues;
    const errors = {
      name: "",
      capacity: "",
      weight: "",
      volume: "",
      defaultStartLocation: ""
    };

    if (!name.trim()) {
      errors.name = "Name is required";
    }

    if (inputValues.defaultEndLocation && !inputValues.defaultStartLocation) {
      errors.defaultStartLocation =
        "Default start location is mandatory when the default end location is set.";
    }

    ["capacity", "weight", "volume"].forEach(item =>
      checkForErrors(item, errors)
    );

    const errorsStr = Object.keys(errors).reduce(
      (sum, el) => sum + errors[el],
      ""
    );
    if (!errorsStr) saveVehicleChanges();
    else {
      setLoading(false);
    }
    setErrors(errors);
  };

  const warnClose = saveDisabled
    ? handleClose
    : () =>
        setChangesDialog({
          visible: true,
          action: handleClose
        });

  const handleTagSelect = tag => {
    if (selectedTags.includes(tag.id)) {
      setSelectedTags(
        selectedTags.filter(selectedTag => selectedTag !== tag.id)
      );
    } else {
      setSelectedTags([...selectedTags, tag.id]);
    }
    setSaveDisabled(false);
  };

  const handleAddressSelect = (field, value) => {
    if (value && value.place_id) {
      // Get place details
      const service = new window.google.maps.places.PlacesService(
        window.document.createElement("div")
      );
      service.getDetails(
        {
          placeId: value.place_id,
          language: "en",
          fields: ["address_components", "geometry", "formatted_address"]
        },
        function (PlaceResult, PlacesServiceStatus) {
          console.log(value, PlaceResult);

          setInputValues({ ...inputValues, [field]: PlaceResult });
        }
      );
    } else {
      setInputValues({ ...inputValues, [field]: value });
    }

    setSaveDisabled(false);
  };

  const autoFocusInput = useRef();

  useEffect(() => {
    if (open && autoFocusInput.current) autoFocusInput.current.focus();
  }, [open]);

  return (
    <>
      <StyledDrawer
        open={open}
        handleClose={warnClose}
        loading={loading}
        // noAnim
      >
        <Box
          className={classes.header}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          px={3}
        >
          <OptimizedTooltip placement="bottom" title="Close">
            <Box ml={-1} mr={2}>
              <IconButton
                onClick={() => {
                  warnClose();
                  // setBackFromEditAction(true);
                }}
              >
                <BackIcon />
              </IconButton>
            </Box>
          </OptimizedTooltip>
          <Box alignSelf="center" overflow="hidden">
            <Typography className={classes.title} variant="h5">
              {vehicleType.name ? "Edit vehicle" : "Add vehicle"}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column">
          <Box pt={4} pl={3} pr={5} mb={1.5}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography className={classes.subtitle}>
                Vehicle attributes
              </Typography>
            </Box>
            <Grid item xs ref={inputRef}>
              {/* Form */}
              <Form
                data={[
                  [
                    {
                      renderInputField: true,
                      label: "License plate or name*",
                      name: "name",
                      type: "text",
                      autoFocus: true,
                      required: "*Required",
                      inputRef: autoFocusInput,
                      dataCy: "vehicle-name"
                    }
                  ],
                  [
                    {
                      id: "default-driver",
                      label: "Default driver",
                      name: "defaultDriver",
                      options: drivers.map(driver => ({
                        name: `${driver.first_name} ${driver.last_name}`,
                        email: `${driver.email}`,
                        id: driver.id
                      })),
                      // shrink: true,
                      // dataCy: "tag-select-type",
                      onChange: (e, v) => {
                        setSaveDisabled(false);
                        setInputValues({
                          ...inputValues,
                          defaultDriver: v
                        });
                      },
                      componentType: "autoComplete"
                    }
                  ],
                  [
                    {
                      id: "default-start",
                      label: "Default start location",
                      name: "defaultStartLocation",
                      width: 384,
                      // shrink: true,
                      // dataCy: "tag-select-type",
                      handleAddressSelect: val => {
                        handleAddressSelect("defaultStartLocation", val);
                      },
                      componentType: "vehicleAutoComplete"
                    }
                  ],
                  [
                    {
                      id: "default-end",
                      label: "Default end location",
                      name: "defaultEndLocation",

                      width: 384,
                      // shrink: true,
                      // dataCy: "tag-select-type",
                      handleAddressSelect: val => {
                        handleAddressSelect("defaultEndLocation", val);
                      },
                      componentType: "vehicleAutoComplete"
                    }
                  ],
                  [
                    {
                      renderInputField: true,
                      label: "Max. units",
                      name: "capacity",
                      type: "number",
                      dataCy: "capacity",
                      onKeyDown: e =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault(),
                      onPaste: e => {
                        numberValidationRegex.test(
                          e.clipboardData.getData("Text")
                        ) && e.preventDefault();
                      },
                      min: 1
                    },
                    {
                      renderInputField: true,
                      label: "Max. weight",
                      name: "weight",
                      type: "number",
                      dataCy: "weight",
                      onKeyDown: e =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault(),
                      onPaste: e => {
                        numberValidationRegex.test(
                          e.clipboardData.getData("Text")
                        ) && e.preventDefault();
                      },
                      min: 1
                    },
                    {
                      renderInputField: true,
                      label: "Max. volume",
                      name: "volume",
                      type: "number",
                      dataCy: "volume",
                      onKeyDown: e =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault(),
                      onPaste: e => {
                        numberValidationRegex.test(
                          e.clipboardData.getData("Text")
                        ) && e.preventDefault();
                      },
                      min: 1
                    }
                  ],
                  [
                    {
                      labelId: "tag-vehicle",
                      selectId: "tag-vehicle-select",
                      label: "Tags",
                      name: "tag",
                      type: "vehicle",
                      choose: false,
                      options: sortedTags,
                      // shrink: true,
                      selected: selectedTags,
                      // dataCy: "tag-select-type",
                      onChange: handleTagSelect,
                      componentType: "tagSelect"
                    }
                  ]
                ]}
                handleChange={handleChange}
                inputValues={inputValues}
                errorValues={errors}
              />
            </Grid>
          </Box>

          {/* Save */}
          <EditDialogActions
            handleClose={warnClose}
            handleClick={handleSave}
            disabled={saveDisabled}
          />
        </Box>
      </StyledDrawer>
      <UnsavedChangesDialog
        open={changesDialog.visible}
        handleClose={() => {
          setChangesDialog({ visible: false, action: () => {} });
        }}
        action={() => {
          changesDialog.action();
          setSaveDisabled(true);
        }}
      />
    </>
  );
}

function mapStateToProps(state) {
  return {
    editId: state.extendable.id,
    solution: state.project.solution,
    vehicles: state.project.vehicles,
    vehicleTypes: state.project.vehicleTypes,
    sortedTags: state.account.sortedTags,
    tags: state.account.tags,
    drivers: state.project.drivers || []
  };
}

export default connect(mapStateToProps, {
  setVehicles,
  setVehicleTypes,
  setSolution
})(withSnackbar(EditVehicle));
